import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Banner, Button, PageHeader, Spinner, StatusPill, Tabs } from '../../../ui'
import { CategoryMeta } from '../commsInboxView/ReplyDetailPane'
import { humanise } from '../commsInboxView/shared'
import { MailboxRef } from '../commsShared/mailboxes'
import { ConversationList, ReceivedView, readReceivedView, rememberReceivedView } from './ConversationList'
import { ExportSlice, OverviewTab, SesHealth, SweepCard, sliceKey } from './OverviewTab'
import { downloadCommsExport } from '../commsShared/exportDownload'
import { ALL_MAILBOXES, Bucket, ConversationState, MailboxFunnel, OverviewResponse, STATE_LABEL, isBucket } from './types'
import styles from './styles.module.scss'

type Tab = 'overview' | 'received' | 'sent'
const TABS: Tab[] = ['overview', 'received', 'sent']
export { ALL_MAILBOXES }

/**
 * One mailbox, three views: how mail is flowing (Overview), the conversations
 * we received (Received) and the letters and replies we sent (Sent).
 * URL: /comms/mailboxes/:address?tab=received&view=threads|emails&bucket=letter:initial_loc&category=…&claim=…&state=…&conversation=…
 */
export const CommsMailboxWorkspaceView = () => {
    const { address: rawAddress = '' } = useParams<{ address: string }>()
    const address = decodeURIComponent(rawAddress).toLowerCase()
    /** /comms/mailboxes/all is the cross-mailbox view: same tabs, no sweep card. */
    const isAll = address === ALL_MAILBOXES
    const mailbox = isAll ? null : address
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const initial = useRef({
        tab: (TABS as string[]).includes(searchParams.get('tab') || '') ? (searchParams.get('tab') as Tab) : ('overview' as Tab),
        bucket: isBucket(searchParams.get('bucket')) ? (searchParams.get('bucket') as Bucket) : ('all' as const),
        category: searchParams.get('category') || '',
        claim: searchParams.get('claim') || '',
        state: ((searchParams.get('state') || '') in STATE_LABEL ? searchParams.get('state') : '') as '' | ConversationState,
        conversation: searchParams.get('conversation') || null,
        view: (searchParams.get('view') === 'emails' || searchParams.get('view') === 'threads' ? searchParams.get('view') : readReceivedView()) as ReceivedView,
    }).current

    const [tab, setTab] = useState<Tab>(initial.tab)
    // Threads or emails: decides what every figure under Received counts (tab badges, cards, list). Threads by default.
    const [view, setViewState] = useState<ReceivedView>(initial.view)
    const setView = (next: ReceivedView) => {
        setViewState(next)
        rememberReceivedView(next)
    }
    const [overview, setOverview] = useState<OverviewResponse | null>(null)
    const [sweep, setSweep] = useState<SweepCard | null>(null)
    const [sesHealth, setSesHealth] = useState<SesHealth | null>(null)
    const [loading, setLoading] = useState(true)
    const [selectedConversation, setSelectedConversation] = useState<string | null>(initial.conversation)
    type ListFilters = { bucket: Bucket | 'all'; category: string; claim: string; state: '' | ConversationState }
    const [listFilters, setListFilters] = useState<ListFilters>({ bucket: initial.bucket, category: initial.category, claim: initial.claim, state: initial.state })
    // Remount the list when the overview pushes a new slice into it.
    const [listKey, setListKey] = useState(0)

    const loadOverview = useCallback(async () => {
        try {
            const [ov, mb] = await Promise.all([api({ url: 'comms/inbox/overview', data: { sourceMailbox: mailbox } }), mailbox ? api({ url: 'comms/mailboxes/list', data: {} }).catch(() => null) : null])
            if (ov.data?.success) setOverview(ov.data)
            if (mb?.data?.success) {
                const card = (mb.data.mailboxes || []).find((m: SweepCard) => m.address.toLowerCase() === address) ?? null
                setSweep(card)
                setSesHealth(card?.live ? mb.data.sesHealth ?? null : null)
            }
        } catch (err: any) {
            Report.failure('Load failed', err?.response?.data?.message || 'Could not load this mailbox.', 'Okay')
        } finally {
            setLoading(false)
        }
    }, [mailbox, address])

    useEffect(() => {
        loadOverview()
    }, [loadOverview])

    // Shareable URL: tab, slice and selected conversation.
    useEffect(() => {
        const next = new URLSearchParams()
        if (tab !== 'overview') next.set('tab', tab)
        if (tab === 'received' && view === 'emails') next.set('view', view)
        if (tab !== 'overview' && listFilters.bucket !== 'all') next.set('bucket', listFilters.bucket)
        if (tab !== 'overview' && listFilters.category) next.set('category', listFilters.category)
        if (tab !== 'overview' && listFilters.claim) next.set('claim', listFilters.claim)
        if (tab !== 'overview' && listFilters.state) next.set('state', listFilters.state)
        if (tab !== 'overview' && selectedConversation) next.set('conversation', selectedConversation)
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true })
    }, [tab, view, listFilters, selectedConversation, searchParams, setSearchParams])

    const funnel: MailboxFunnel | null = useMemo(() => {
        if (!overview) return null
        if (!isAll) return overview.mailboxes.find((m) => m.address.toLowerCase() === address) ?? null
        return { ...overview.totals, address: ALL_MAILBOXES, mailbox: { address: ALL_MAILBOXES, displayName: 'All mailboxes', provider: 'all', enabled: true, live: false, defaultPartyType: null } }
    }, [overview, isAll, address])
    const registered: (MailboxRef & { defaultPartyType: string | null }) | null = isAll ? null : overview?.registeredMailboxes.find((m) => m.address.toLowerCase() === address) ?? funnel?.mailbox ?? null

    const categoryMeta = useMemo(() => {
        const map = new Map<string, CategoryMeta>()
        for (const c of funnel?.byCategory ?? []) if (c.key) map.set(c.key, { key: c.key, label: c.label, description: c.description, nextActionLabel: c.nextAction })
        return map
    }, [funnel])
    const categoryOptions = useMemo(() => Array.from(categoryMeta.values()).map((c) => ({ value: c.key, label: c.label })), [categoryMeta])

    const openBucket = (bucket: Bucket | 'all', category: string | null = null, openIn: ReceivedView | null = null) => {
        // A card on the Overview knows whether its figure counts threads or emails; open the matching view.
        if (openIn) setView(openIn)
        setListFilters({ bucket, category: category ?? '', claim: '', state: '' })
        setSelectedConversation(null)
        setListKey((k) => k + 1)
        setTab('received')
    }

    const onFiltersChanged = useCallback((f: ListFilters) => setListFilters(f), [])

    // Category / letter-type reports scoped to this mailbox (same file the Categories page builds).
    const [exporting, setExporting] = useState<string | null>(null)
    const exportSlice = async (slice: ExportSlice) => {
        setExporting(sliceKey(slice))
        try {
            await downloadCommsExport({ ...slice, mailbox: address, includeUnlinked: true, rowPerEmail: true, includeArchived: slice.kind === 'thread_outcome' }, `comms-${address}-${slice.kind === 'thread_outcome' ? 'threads-' : ''}${slice.sendKind ?? ''}-${(slice.category ?? 'all').replace(/^__/, '')}${slice.groupBy ? `-by-${slice.groupBy}` : ''}.xlsx`)
        } finally {
            setExporting(null)
        }
    }

    const title = isAll ? 'All mailboxes' : registered?.displayName || address
    const partyHint = registered?.defaultPartyType && registered.defaultPartyType !== 'unknown' ? `mostly ${registered.defaultPartyType} mail` : null

    return (
        <div className={styles.stack}>
            <PageHeader
                eyebrow="Comms Management · Mailboxes"
                title={
                    <>
                        {title}
                        {registered && (
                            <StatusPill size="sm" tone={registered.live ? 'good' : registered.enabled === false ? 'neutral' : 'info'} dot>
                                {registered.live ? 'Live (SES)' : registered.enabled === false ? 'Disabled' : humanise(registered.provider)}
                            </StatusPill>
                        )}
                    </>
                }
                intro={
                    isAll ? (
                        <>
                            Every mailbox together, measured the same way.
                            {funnel ? ` ${funnel.held.toLocaleString('en-GB')} emails downloaded across ${overview?.mailboxes.length ?? 0} mailboxes.` : ''}
                        </>
                    ) : (
                        <>
                            <span className={styles.mono}>{address}</span>
                            {partyHint ? ` · ${partyHint}` : ''}
                            {funnel ? ` · ${funnel.held.toLocaleString('en-GB')} emails downloaded` : ''}
                        </>
                    )
                }
                actions={
                    <div className={styles.headerActions}>
                        <Button size="sm" variant="ghost" onClick={() => navigate('/comms/mailboxes')}>
                            ← All mailboxes
                        </Button>
                        <Button size="sm" variant="ghost" onClick={loadOverview} disabled={loading}>
                            Refresh
                        </Button>
                    </div>
                }
            />

            {!loading && !isAll && !registered && !funnel && <Banner tone="warn">No mailbox is registered under this address and no emails carry it. Check the address or add the mailbox on the Mailboxes page.</Banner>}

            <Tabs<Tab>
                value={tab}
                onChange={(t) => {
                    setTab(t)
                    if (t === 'overview') setSelectedConversation(null)
                }}
                items={[
                    { value: 'overview', label: 'Overview' },
                    // Same unit as everything under it: threads with mail received, or emails received.
                    { value: 'received', label: 'Received', count: view === 'threads' ? funnel?.conversations?.withInbound ?? funnel?.held : funnel?.held },
                    { value: 'sent', label: 'Sent' },
                ]}
                noMargin
            />

            {loading && !overview ? (
                <Spinner block text="Loading mailbox…" />
            ) : tab === 'overview' ? (
                <OverviewTab funnel={funnel} sweep={sweep} sesHealth={sesHealth} onOpenBucket={openBucket} onExport={exportSlice} exporting={exporting} view={view} onViewChange={setView} />
            ) : (
                <ConversationList
                    key={`${tab}-${listKey}`}
                    mailbox={mailbox}
                    direction={tab === 'sent' ? 'sent' : 'received'}
                    counts={funnel?.conversations ?? null}
                    emails={funnel}
                    categoryMeta={categoryMeta}
                    categoryOptions={categoryOptions}
                    selectedId={selectedConversation}
                    onSelect={setSelectedConversation}
                    view={view}
                    onViewChange={setView}
                    initialBucket={listFilters.bucket}
                    initialCategory={listFilters.category}
                    initialClaim={listFilters.claim}
                    initialState={listFilters.state}
                    onFiltersChanged={onFiltersChanged}
                    onChanged={loadOverview}
                />
            )}
        </div>
    )
}
